import React, { useMemo, useState } from 'react';
import { StepProps, SPECIALTY_COLORS, Specialty } from '../../types';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Droplets, Heart, FlaskConical, Stethoscope, MessageCircle, Target } from 'lucide-react';
import { playSuccess, playWrong } from '../../utils/sounds';
import { Celebration } from '../Celebration';
import { shuffle } from '../../utils/shuffle';
type Stage = 'Early' | 'Early-Mid' | 'Mid' | 'Late';
const STAGES: Stage[] = ['Early', 'Early-Mid', 'Mid', 'Late'];
const CORRECT_PLACEMENT: Record<Specialty, Stage> = {
  PCP: 'Early',
  Endocrinologist: 'Early-Mid',
  Cardiologist: 'Mid',
  Nephrologist: 'Late'
};
const SPECIALTY_ICONS: Record<Specialty, React.ElementType> = {
  Nephrologist: Droplets,
  Cardiologist: Heart,
  Endocrinologist: FlaskConical,
  PCP: Stethoscope
};
const STAGE_DESCRIPTIONS: Record<Stage, string> = {
  Early: 'Initial diagnosis, low risk, preventative care',
  'Early-Mid': 'Emerging complications, metabolic management',
  Mid: 'Established disease, CV/renal events emerging',
  Late: 'Advanced/high-risk, specialist-led management'
};
const REVEAL_INSIGHTS: Record<Specialty, string> = {
  PCP: 'PCPs see patients earliest — often before CKD is even diagnosed. They manage early-stage risk factors and decide when to refer.',
  Endocrinologist: "Endocrinologists retain patients through early metabolic management. They're the bridge between prevention and specialist care.",
  Cardiologist: 'Cardiologists typically encounter CKD patients when CV events emerge — mid-stage, where dual organ protection becomes critical.',
  Nephrologist: 'Nephrologists receive patients late — often after significant kidney damage. This shapes their cautious, safety-first approach.'
};
const ALL_SPECIALTIES: Specialty[] = ['PCP', 'Endocrinologist', 'Cardiologist', 'Nephrologist'];
export function PatientStaging({
  state,
  nextStep
}: StepProps) {
  const color = SPECIALTY_COLORS[state.selectedSpecialty || 'Nephrologist'];
  const [placements, setPlacements] = useState<Record<string, Stage | null>>({
    PCP: null,
    Endocrinologist: null,
    Cardiologist: null,
    Nephrologist: null
  });
  const [activeSpecialty, setActiveSpecialty] = useState<Specialty | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [celebration, setCelebration] = useState<'success' | 'wrong' | null>(null);
  const shuffledSpecialties = useMemo(() => shuffle([...ALL_SPECIALTIES]), []);
  const unplacedSpecialties = shuffledSpecialties.filter(s => !placements[s]);
  const allPlaced = unplacedSpecialties.length === 0;
  const handleStageClick = (stage: Stage) => {
    if (revealed || !activeSpecialty) return;
    setPlacements(prev => ({
      ...prev,
      [activeSpecialty]: stage
    }));
    setActiveSpecialty(null);
  };
  const handleSpecialtyClick = (spec: Specialty) => {
    if (revealed) return;
    // If already placed, unplace it
    if (placements[spec]) {
      setPlacements(prev => ({
        ...prev,
        [spec]: null
      }));
      setActiveSpecialty(null);
      return;
    }
    setActiveSpecialty(spec === activeSpecialty ? null : spec);
  };
  const handleReveal = () => {
    setRevealed(true);
    const correctCount = ALL_SPECIALTIES.filter(s => placements[s] === CORRECT_PLACEMENT[s]).length;
    if (correctCount >= 3) {
      playSuccess();
      setCelebration('success');
    } else {
      playWrong();
      setCelebration('wrong');
    }
    setTimeout(() => setCelebration(null), 2000);
  };
  const getSpecialtiesInStage = (stage: Stage) => ALL_SPECIALTIES.filter(s => placements[s] === stage);
  const correctCount = ALL_SPECIALTIES.filter(s => placements[s] === CORRECT_PLACEMENT[s]).length;
  return <div className="flex-grow flex flex-col py-12 px-6 overflow-y-auto pt-20" data-id="element-309">
      <Celebration type={celebration} color={color} data-id="element-310" />
      <div className="max-w-5xl w-full mx-auto" data-id="element-311">
        <div className="text-center mb-8" data-id="element-312">
          <div className="inline-flex items-center justify-center p-3 rounded-full mb-4 bg-brand/10 text-brand" data-id="element-313">
            <Target size={28} data-id="element-314" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-dark mb-3" data-id="element-315">
            Where does each specialty meet the patient?
          </h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto" data-id="element-316">
            Place each specialty on the treatment stage where they typically
            encounter CKD patients.
            {!revealed && activeSpecialty && <span className="block mt-2 font-semibold" style={{
            color: SPECIALTY_COLORS[activeSpecialty]
          }} data-id="element-317">
                Now click a stage to place {activeSpecialty}s
              </span>}
          </p>
        </div>

        {/* Specialty chips to place */}
        {!revealed && <div className="flex flex-wrap justify-center gap-3 mb-8" data-id="element-318">
            {shuffledSpecialties.map(spec => {
          const Icon = SPECIALTY_ICONS[spec];
          const isPlaced = !!placements[spec];
          const isActive = activeSpecialty === spec;
          const specColor = SPECIALTY_COLORS[spec];
          return <motion.button key={spec} whileHover={{
            scale: 1.03
          }} whileTap={{
            scale: 0.97
          }} onClick={() => handleSpecialtyClick(spec)} className={`flex items-center px-4 py-3 rounded-xl border-2 font-semibold transition-all duration-200 ${isPlaced ? 'opacity-40 border-gray-200 bg-gray-50' : isActive ? 'shadow-lg bg-white' : 'bg-white border-gray-200 hover:border-gray-300'}`} style={{
            borderColor: isActive ? specColor : isPlaced ? undefined : undefined,
            boxShadow: isActive ? `0 0 0 3px ${specColor}30` : undefined
          }} data-id="element-319">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center mr-2" style={{
              backgroundColor: `${specColor}15`,
              color: specColor
            }} data-id="element-320">
                    <Icon size={18} data-id="element-321" />
                  </div>
                  <span className={isPlaced ? 'text-gray-400 line-through' : 'text-dark'} data-id="element-322">
                    {spec}
                  </span>
                  {isPlaced && <span className="ml-2 text-xs text-gray-400" data-id="element-323">
                      ({placements[spec]})
                    </span>}
                </motion.button>;
        })}
          </div>}

        {/* Treatment Stage Timeline */}
        <div className="relative mb-10" data-id="element-324">
          {/* Connecting line */}
          <div className="absolute top-1/2 left-0 right-0 h-1 bg-gray-200 rounded-full -translate-y-1/2 hidden md:block" style={{
          top: '2rem'
        }} data-id="element-325" />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-326">
            {STAGES.map((stage, i) => {
            const specialtiesHere = getSpecialtiesInStage(stage);
            const isClickable = !revealed && !!activeSpecialty;
            const correctSpecialtiesHere = revealed ? ALL_SPECIALTIES.filter(s => CORRECT_PLACEMENT[s] === stage) : [];
            return <motion.button key={stage} onClick={() => handleStageClick(stage)} disabled={!isClickable} className={`relative rounded-2xl border-2 p-5 text-center transition-all duration-200 ${isClickable ? 'border-gray-300 hover:border-gray-400 hover:bg-gray-50 cursor-pointer' : 'border-gray-200 cursor-default'} ${revealed ? 'bg-gray-50' : 'bg-white'}`} whileHover={isClickable ? {
              scale: 1.02
            } : {}} whileTap={isClickable ? {
              scale: 0.98
            } : {}} data-id="element-327">
                  {/* Stage dot */}
                  <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center mx-auto mb-3 relative z-10" data-id="element-328">
                    <span className="text-sm font-bold text-gray-600" data-id="element-329">
                      {i + 1}
                    </span>
                  </div>
                  <h4 className="font-bold text-dark mb-1" data-id="element-330">{stage}</h4>
                  <p className="text-xs text-gray-400 mb-4" data-id="element-331">
                    {STAGE_DESCRIPTIONS[stage]}
                  </p>

                  {/* Placed specialties (before reveal) */}
                  {!revealed && specialtiesHere.length > 0 && <div className="space-y-2" data-id="element-332">
                      {specialtiesHere.map(spec => {
                  const Icon = SPECIALTY_ICONS[spec];
                  return <motion.div key={spec} initial={{
                    scale: 0.8,
                    opacity: 0
                  }} animate={{
                    scale: 1,
                    opacity: 1
                  }} className="flex items-center justify-center px-3 py-2 rounded-lg text-sm font-semibold text-white" style={{
                    backgroundColor: SPECIALTY_COLORS[spec]
                  }} data-id="element-333">
                            <Icon size={14} className="mr-1.5" data-id="element-334" />
                            {spec}
                          </motion.div>;
                })}
                    </div>}

                  {/* Correct placements (after reveal) */}
                  {revealed && correctSpecialtiesHere.map(spec => {
                const Icon = SPECIALTY_ICONS[spec];
                const userPlacement = placements[spec];
                const isCorrect = userPlacement === stage;
                return <motion.div key={spec} initial={{
                  scale: 0.8,
                  opacity: 0
                }} animate={{
                  scale: 1,
                  opacity: 1
                }} transition={{
                  delay: 0.3 + ALL_SPECIALTIES.indexOf(spec) * 0.15
                }} className="mb-2" data-id="element-335">
                          <div className="flex items-center justify-center px-3 py-2 rounded-lg text-sm font-semibold text-white" style={{
                    backgroundColor: SPECIALTY_COLORS[spec]
                  }} data-id="element-336">
                            <Icon size={14} className="mr-1.5" data-id="element-337" />
                            {spec}
                          </div>
                          <span className={`text-xs font-bold mt-1 block ${isCorrect ? 'text-green-600' : 'text-red-500'}`} data-id="element-338">
                            {isCorrect ? '✓ Correct' : `You said: ${userPlacement}`}
                          </span>
                        </motion.div>;
              })}

                  {/* Empty state */}
                  {!revealed && specialtiesHere.length === 0 && <div className={`h-12 border-2 border-dashed rounded-lg flex items-center justify-center ${isClickable ? 'border-gray-300' : 'border-gray-200'}`} data-id="element-339">
                      <span className="text-xs text-gray-300" data-id="element-340">Drop here</span>
                    </div>}
                </motion.button>;
          })}
          </div>
        </div>

        {/* Reveal button */}
        {!revealed && <div className="flex justify-center h-16" data-id="element-341">
            {allPlaced ? <motion.button initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} onClick={handleReveal} className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-dark rounded-full hover:bg-black transition-colors shadow-lg" data-id="element-342">
                Reveal the Answer
                <ArrowRight className="ml-2 w-5 h-5" data-id="element-343" />
              </motion.button> : <p className="text-gray-400 text-sm self-center" data-id="element-344">
                {activeSpecialty ? `Click a stage to place ${activeSpecialty}s` : `Select a specialty above, then click a stage (${4 - Object.values(placements).filter(Boolean).length} remaining)`}
              </p>}
          </div>}

        {/* Post-reveal insights */}
        <AnimatePresence data-id="element-345">
          {revealed && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} data-id="element-346">
              <div className={`rounded-2xl p-5 mb-8 text-center ${correctCount >= 3 ? 'bg-green-50 border border-green-200' : correctCount >= 2 ? 'bg-amber-50 border border-amber-200' : 'bg-red-50 border border-red-200'}`} data-id="element-347">
                <p className={`text-lg font-bold ${correctCount >= 3 ? 'text-green-800' : correctCount >= 2 ? 'text-amber-800' : 'text-red-800'}`} data-id="element-348">
                  Your group placed {correctCount} of 4 correctly
                </p>
              </div>

              {/* Insights per specialty */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8" data-id="element-349">
                {ALL_SPECIALTIES.map((spec, i) => {
              const Icon = SPECIALTY_ICONS[spec];
              const specColor = SPECIALTY_COLORS[spec];
              return <motion.div key={spec} initial={{
                opacity: 0,
                y: 15
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.5 + i * 0.15
              }} className="bg-white rounded-xl border border-gray-200 p-5" data-id="element-350">
                      <div className="flex items-center mb-3" data-id="element-351">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center mr-3" style={{
                    backgroundColor: `${specColor}15`,
                    color: specColor
                  }} data-id="element-352">
                          <Icon size={18} data-id="element-353" />
                        </div>
                        <div data-id="element-354">
                          <span className="font-bold text-dark" data-id="element-355">{spec}s</span>
                          <span className="text-xs font-bold uppercase tracking-wider ml-2 px-2 py-0.5 rounded" style={{
                      backgroundColor: `${specColor}15`,
                      color: specColor
                    }} data-id="element-356">
                            {CORRECT_PLACEMENT[spec]}
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 leading-relaxed" data-id="element-357">
                        {REVEAL_INSIGHTS[spec]}
                      </p>
                    </motion.div>;
            })}
              </div>

              <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6 mb-8 flex items-start" data-id="element-358">
                <MessageCircle className="text-blue-500 mr-4 mt-1 flex-shrink-0" size={24} data-id="element-359" />
                <div data-id="element-360">
                  <h4 className="text-blue-900 font-bold mb-2" data-id="element-361">
                    Discuss with your group:
                  </h4>
                  <ul className="text-blue-800 space-y-1.5" data-id="element-362">
                    <li data-id="element-363">
                      <strong data-id="element-364">Targeting:</strong> Where on this timeline is the
                      biggest opportunity to change outcomes?
                    </li>
                    <li data-id="element-365">
                      <strong data-id="element-366">Handoffs:</strong> What happens between stages?
                      Where do patients fall through the cracks?
                    </li>
                    <li data-id="element-367">
                      <strong data-id="element-368">Your specialty:</strong> How does knowing when{' '}
                      {state.selectedSpecialty || 'your specialty'} meets the
                      patient change your approach?
                    </li>
                  </ul>
                </div>
              </div>

              <div className="flex justify-center pb-8" data-id="element-369">
                <button onClick={nextStep} className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-dark rounded-full hover:bg-black transition-colors" data-id="element-370">
                  Now Pick Your Arguments
                  <ArrowRight className="ml-2 w-5 h-5" data-id="element-371" />
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}